import * as React from "react";
import * as PopoverPrimitive from "@radix-ui/react-popover";

import { cn } from "@/lib/utils";

function Popover({
  ...props
}: React.ComponentProps<typeof PopoverPrimitive.Root>) {
  return <PopoverPrimitive.Root data-slot="popover" {...props} />;
}

function PopoverTrigger({
  ...props
}: React.ComponentProps<typeof PopoverPrimitive.Trigger>) {
  return <PopoverPrimitive.Trigger data-slot="popover-trigger" {...props} />;
}

/**
 * When the popover opens inside a Bootstrap modal, portalling into
 * `document.body` (Radix's default) puts the content outside the modal's
 * DOM subtree. Bootstrap's focus-trap then yanks focus back whenever Radix
 * auto-focuses the content, Radix sees "focus left the popover", and the
 * popover closes itself instantly. Portalling into the currently-shown
 * modal root keeps the popover inside Bootstrap's focus-trap scope.
 */
function useModalAwarePortalContainer() {
  // Run the DOM query on every render of PopoverContent. PopoverContent is
  // mounted while the popover is open, so at least one render coincides
  // with the modal being present on screen. A useState-based lazy init
  // would miss it when Radix renders the content before Bootstrap has
  // finished adding the `.show` class.
  return document.querySelector<HTMLElement>(".modal.show");
}

function PopoverContent({
  className,
  align = "center",
  sideOffset = 4,
  onOpenAutoFocus,
  onCloseAutoFocus,
  onFocusOutside,
  onInteractOutside,
  ...props
}: React.ComponentProps<typeof PopoverPrimitive.Content>) {
  // Ground truth for the "inside a Bootstrap modal" workaround: the same
  // container lookup that portals the popover. If it's null, the popover is
  // NOT inside a modal — so every Bootstrap-modal compensation below must
  // stay off the event path and let Radix's defaults run.
  const container = useModalAwarePortalContainer();
  const insideModal = container !== null;
  return (
    <PopoverPrimitive.Portal container={container ?? undefined}>
      <PopoverPrimitive.Content
        data-slot="popover-content"
        data-inside-modal={insideModal ? "true" : undefined}
        align={align}
        sideOffset={sideOffset}
        // Coexistence with Bootstrap modals: Bootstrap installs a global
        // focus-trap that re-focuses the modal whenever focus leaves it.
        // Radix auto-focuses PopoverContent on open, kicks off the trap,
        // and then reads the bounced-back focus as "focus left the
        // popover" → dismisses. We:
        //   - skip auto-focus on open so the trap never fires;
        //   - preventDefault the close auto-focus so Bootstrap doesn't
        //     fight the focus we hand back;
        //   - swallow focus/interact-outside events — those are always the
        //     trap, not a real dismiss click (the modal's own close button +
        //     backdrop still dismiss it).
        // All four compensations only apply when the popover is actually
        // inside a modal. Outside a modal they would break normal popover
        // behaviour (auto-focus, click-outside dismissal).
        onOpenAutoFocus={(event) => {
          if (insideModal) event.preventDefault();
          onOpenAutoFocus?.(event);
        }}
        onCloseAutoFocus={(event) => {
          if (insideModal) event.preventDefault();
          onCloseAutoFocus?.(event);
        }}
        onFocusOutside={(event) => {
          if (insideModal) event.preventDefault();
          onFocusOutside?.(event);
        }}
        onInteractOutside={(event) => {
          if (insideModal) event.preventDefault();
          onInteractOutside?.(event);
        }}
        className={cn(
          "bg-popover text-popover-foreground data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95 data-[side=bottom]:slide-in-from-top-2 data-[side=left]:slide-in-from-right-2 data-[side=right]:slide-in-from-left-2 data-[side=top]:slide-in-from-bottom-2 z-50 w-72 origin-(--radix-popover-content-transform-origin) border-box shadow-md outline-hidden",
          className,
        )}
        {...props}
      />
    </PopoverPrimitive.Portal>
  );
}

function PopoverAnchor({
  ...props
}: React.ComponentProps<typeof PopoverPrimitive.Anchor>) {
  return <PopoverPrimitive.Anchor data-slot="popover-anchor" {...props} />;
}

export { Popover, PopoverTrigger, PopoverContent, PopoverAnchor };
